// app/projects/SkillHeatmap.jsx
import { useMemo, useState } from 'react';
import { SCORE_TINT, SCORE_FG, scoreBand as scoreLevel } from '../ds';
import { scoreBand } from './scoring';

// Cell fills use the score family's TINTS (a heatmap cell is a large fill —
// saturated marks would shout; the printed score carries the exact value),
// one tint PER LEVEL — the old low/mid/high banding made a 1 and a 2
// indistinguishable at a glance. Legend swatches use the same values, so the
// legend can never drift from the cells again (the old CSS carried a #6ee7b7
// that existed in no palette).

const SORT_OPTIONS = [
  { key: 'mastery', label: 'Lowest mastery' },
  { key: 'alpha', label: 'A → Z' },
  { key: 'area', label: 'By area' },
];

// `onCellClick(student, skillRow)` opens the grade-detail modal (score
// rationale + evidence) for a graded or n/a cell; `onStudentClick(student)`
// opens the submission viewer — the host (ClassOverview) owns both. Nothing
// in the matrix navigates away: the disc lives behind the Students table's
// "View disc" link and the grade modal's labeled link. Truly empty cells
// (never graded) aren't clickable: there is nothing to explain.
export default function SkillHeatmap({
  skills,
  students,
  matrix,
  specificSkillIdFilter,
  onCellClick,
  onStudentClick,
}) {
  const [sortBy, setSortBy] = useState('mastery');
  const [studentSort, setStudentSort] = useState('alpha'); // 'alpha' | 'lowest'

  // Index matrix for O(1) lookups
  const cellMap = useMemo(() => {
    const map = new Map();
    for (const c of matrix || []) {
      map.set(`${c.student_id}::${c.specific_skill_id}`, c.score);
    }
    return map;
  }, [matrix]);

  // Filter + sort skills (rows)
  const visibleSkills = useMemo(() => {
    let list = skills;
    if (specificSkillIdFilter) {
      list = list.filter((s) => specificSkillIdFilter.has(s.specific_skill_id));
    }
    const sorted = [...list].sort((a, b) => {
      if (sortBy === 'alpha') {
        return a.specific_skill.localeCompare(b.specific_skill);
      }
      if (sortBy === 'area') {
        const areaCmp = (a.area || '').localeCompare(b.area || '');
        if (areaCmp !== 0) return areaCmp;
        const compCmp = (a.competency || '').localeCompare(b.competency || '');
        if (compCmp !== 0) return compCmp;
        return a.specific_skill.localeCompare(b.specific_skill);
      }
      // 'mastery' — worst first; ungraded last
      const aGraded = a.n_graded > 0;
      const bGraded = b.n_graded > 0;
      if (aGraded !== bGraded) return aGraded ? -1 : 1;
      if (aGraded && bGraded) {
        if (a.mastery_pct !== b.mastery_pct) return a.mastery_pct - b.mastery_pct;
      }
      return a.specific_skill.localeCompare(b.specific_skill);
    });
    return sorted;
  }, [skills, specificSkillIdFilter, sortBy]);

  // Sort students (columns)
  const visibleStudents = useMemo(() => {
    const sorted = [...students].sort((a, b) => {
      if (studentSort === 'lowest') {
        const aAvg = a.overall_avg ?? 999;
        const bAvg = b.overall_avg ?? 999;
        if (aAvg !== bAvg) return aAvg - bAvg;
      }
      return (a.student_name || '').localeCompare(b.student_name || '');
    });
    return sorted;
  }, [students, studentSort]);

  if (visibleSkills.length === 0 || visibleStudents.length === 0) {
    return <div className="hm-empty">No data to display.</div>;
  }

  // Widen the student columns when there are few of them, so the matrix fills
  // the card instead of leaving a big blank gap on the right.
  const n = visibleStudents.length;
  const colW = n <= 5 ? 120 : n <= 8 ? 92 : n <= 12 ? 72 : 56;

  return (
    <>
      <div className="hm-controls">
        <div className="hm-controls-group">
          <span className="hm-controls-label">Sort skills</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`hm-control-btn ${sortBy === opt.key ? 'active' : ''}`}
              onClick={() => setSortBy(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="hm-controls-group">
          <span className="hm-controls-label">Sort students</span>
          <button
            type="button"
            className={`hm-control-btn ${studentSort === 'alpha' ? 'active' : ''}`}
            onClick={() => setStudentSort('alpha')}
          >
            A → Z
          </button>
          <button
            type="button"
            className={`hm-control-btn ${studentSort === 'lowest' ? 'active' : ''}`}
            onClick={() => setStudentSort('lowest')}
          >
            Lowest first
          </button>
        </div>
        <div className="hm-legend">
          <span className="hm-legend-item">
            <span className="hm-legend-swatch" style={{ background: SCORE_TINT[1] }} />1 (re-teach)
          </span>
          <span className="hm-legend-item">
            <span className="hm-legend-swatch" style={{ background: SCORE_TINT[2] }} />2
          </span>
          <span className="hm-legend-item">
            <span className="hm-legend-swatch" style={{ background: SCORE_TINT[3] }} />3 (meeting)
          </span>
          <span className="hm-legend-item">
            <span className="hm-legend-swatch" style={{ background: SCORE_TINT[4] }} />4
          </span>
          <span className="hm-legend-item">
            <span className="hm-legend-swatch" style={{ background: SCORE_TINT[5] }} />5 (exceeds)
          </span>
          <span className="hm-legend-item">
            <span
              className="hm-legend-swatch"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(45deg, #f1f5f9, #f1f5f9 3px, #e2e8f0 3px, #e2e8f0 6px)',
                border: '1px solid #d8e0ea',
              }}
            />
            N/A (work cannot show it)
          </span>
          <span className="hm-legend-item">
            <span
              className="hm-legend-swatch"
              style={{ background: '#eef2f7', border: '1px solid #d8e0ea' }}
            />
            Not graded yet
          </span>
        </div>
      </div>

      <div className="hm-wrap">
        <table className="hm-table" style={{ '--hm-col-w': `${colW}px` }}>
          <thead>
            <tr>
              <th className="hm-corner">
                <span className="hm-corner-label">
                  Skill developmental descriptor ↓ &nbsp; / &nbsp; Student →
                </span>
              </th>
              {visibleStudents.map((st) => {
                return (
                  <th key={st.student_id} className="hm-col-label">
                    <button
                      type="button"
                      onClick={() => onStudentClick?.(st)}
                      className="hm-col-label-inner"
                      title={
                        st.overall_avg != null
                          ? `${st.student_name} · avg ${st.overall_avg.toFixed(1)} · click to open their work`
                          : `${st.student_name} · not yet graded · click to open their work`
                      }
                    >
                      {st.student_name}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleSkills.map((s) => {
              const rowBand = scoreBand(s.class_avg);
              const showParent = s.skill && s.skill !== s.specific_skill;
              return (
                <tr key={s.specific_skill_id}>
                  <th className="hm-row-label">
                    <div className="hm-row-label-text" title={s.specific_skill}>
                      {s.specific_skill}
                    </div>
                    {showParent && (
                      <div
                        className="hm-row-label-parent"
                        title={`Parent skill: ${s.skill}\nCompetency: ${s.competency}\nArea: ${s.area}`}
                      >
                        <span className="hm-row-label-parent-key">Skill:</span> {s.skill}
                      </div>
                    )}
                    <div className="hm-row-label-meta">
                      {s.n_graded > 0 ? (
                        <>
                          <span className={`hm-row-avg hm-row-avg-${rowBand}`}>
                            <span className="hm-row-avg-label">avg</span>
                            <span className="hm-row-avg-value">
                              {s.class_avg != null ? s.class_avg.toFixed(1) : '—'}
                            </span>
                          </span>
                          <span className="hm-row-graded">{s.n_graded} assessed</span>
                        </>
                      ) : (
                        <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                          Not assessed yet
                        </span>
                      )}
                    </div>
                  </th>
                  {visibleStudents.map((st) => {
                    const cellKey = `${st.student_id}::${s.specific_skill_id}`;
                    const hasCell = cellMap.has(cellKey);
                    const score = hasCell ? cellMap.get(cellKey) : undefined;

                    // no grade row for this pair → not graded yet
                    if (!hasCell) {
                      return (
                        <td
                          key={st.student_id}
                          className="hm-cell hm-cell-none"
                          title={`${st.student_name} · ${s.specific_skill}\nNot graded yet`}
                        />
                      );
                    }
                    // graded, but this artifact could not show the skill → N/A
                    if (score == null) {
                      return (
                        <td
                          key={st.student_id}
                          className="hm-cell"
                          style={{
                            backgroundImage:
                              'repeating-linear-gradient(45deg, #f1f5f9, #f1f5f9 4px, #e2e8f0 4px, #e2e8f0 8px)',
                            color: '#94a3b8',
                            fontSize: 10,
                            textAlign: 'center',
                          }}
                        >
                          <button
                            type="button"
                            className="hm-cell-link"
                            onClick={() => onCellClick?.(st, s)}
                            title={`${st.student_name} · ${s.specific_skill}\nNot applicable to this work (assessed, but this artifact cannot show this skill).\nClick for the grader's reason.`}
                          >
                            n/a
                          </button>
                        </td>
                      );
                    }
                    const level = scoreLevel(score);
                    return (
                      <td
                        key={st.student_id}
                        className="hm-cell"
                        style={{ background: SCORE_TINT[level], color: SCORE_FG[level] }}
                      >
                        <button
                          type="button"
                          className="hm-cell-link"
                          onClick={() => onCellClick?.(st, s)}
                          title={`${st.student_name} · ${s.specific_skill}\nScore: ${score.toFixed(1)}\nClick for evidence & rationale.`}
                        >
                          {score % 1 === 0 ? score : score.toFixed(1)}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
